import { ArrowLeft, LayoutTemplate } from 'lucide-react';
import {
  draftSaveIndicator,
  websiteTemplateThumbnailSrc,
  type DraftSaveSnapshot,
  type StoreWebsiteEditorTemplate,
  type TemplatePublicStatus,
  type WebsitePublishAction,
} from '@dukanti/shared';
import { env } from '@dukanti/shared/env';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { CanvasReportedRegion } from '../hooks/use-storefront-canvas-host';
import { useWebsiteT } from '../hooks/use-website-t';
import { DraftSaveStatusChip } from './draft-save-status';
import { EditModeSwitch } from './edit-mode-switch';
import { EditableRegionsMenu } from './editable-regions-menu';
import { OverflowMenu } from './overflow-menu';

export interface StorefrontToolbarProps {
  readonly template: StoreWebsiteEditorTemplate;
  /** `live`: customers see this template now ("Published"); otherwise "Draft". */
  readonly status: TemplatePublicStatus;
  /** This template is live and its draft differs from what customers see. */
  readonly hasUnpublishedChanges: boolean;
  /** Label and state of the publish button (`derivePublishAction`). */
  readonly publishAction: WebsitePublishAction;
  readonly editMode: boolean;
  readonly editableRegions?: readonly CanvasReportedRegion[];
  readonly editableRegionsKey?: string;
  readonly editingLocked?: boolean;
  readonly onEditRegion?: (regionId: string) => void;
  readonly onEditModeChange: (enabled: boolean) => void;
  readonly onBack: () => void;
  readonly onChangeTemplate: () => void;
  readonly isPublishing: boolean;
  readonly onPublish: () => void;
  readonly onOpenLanguage: () => void;
  readonly onOpenLive: () => void;
  /** Null before anything is published. */
  readonly liveUrl: string | null;
  /** Epic #228 P3: present only when a newer version of this template is available. */
  readonly onUpdateTemplate?: () => void;
  /** Epic #228 P4: the auto-save queue of this template, for the save status next to Publish. */
  readonly saveSnapshot: DraftSaveSnapshot;
  readonly onRetrySave: () => void;
  readonly onReloadDraft: () => void;
  /** Opens the item whose value the server refused (a kept rejection). */
  readonly onGoToItem?: (slotId: string) => void;
  /** False while a region dialog with its own save status is open: announce once. */
  readonly saveStatusLive?: boolean;
}

const PUBLISH_LABEL_KEYS: Readonly<Record<WebsitePublishAction['label'], string>> = {
  publish: 'admin.websiteTemplates.editor.publish',
  publishChanges: 'admin.websiteTemplates.editor.publishChanges',
  allPublished: 'admin.websiteTemplates.editor.allPublished',
};

/**
 * The top of the active storefront (Screen 2): back, "Storefront", the
 * template with its thumbnail, name and status (Published or Draft), Change
 * template, the Edit mode switch, "Unpublished changes" next to the publish
 * button, and Publish / Publish changes / All changes published. Store
 * language, the live store and "Update template" are under "More options".
 * There is no Preview, no Save Draft and no property panel.
 *
 * Epic #228 P4 (PD12): near Publish, in order: "Unpublished changes", the
 * save status, the button. When the button already says "All changes
 * published" and everything is saved, the status is not repeated.
 */
export function StorefrontToolbar({
  template,
  status,
  hasUnpublishedChanges,
  publishAction,
  editMode,
  editableRegions = [],
  editableRegionsKey,
  editingLocked = false,
  onEditRegion,
  onEditModeChange,
  onBack,
  onChangeTemplate,
  isPublishing,
  onPublish,
  onOpenLanguage,
  onOpenLive,
  liveUrl,
  onUpdateTemplate,
  saveSnapshot,
  onRetrySave,
  onReloadDraft,
  onGoToItem,
  saveStatusLive = true,
}: StorefrontToolbarProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const name = t(template.titleKey);
  const thumbnail = websiteTemplateThumbnailSrc(template.thumbnailAssetKey, env.VITE_SUPABASE_URL);

  return (
    <header
      aria-label={t('admin.websiteTemplates.frame.toolbar')}
      data-testid="storefront-toolbar"
      className="border-border bg-card flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-3"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('admin.websiteTemplates.editor.back')}
        onClick={onBack}
      >
        <ArrowLeft aria-hidden="true" className="size-5 rtl:rotate-180" />
      </Button>
      <h1 className="font-display text-foreground text-xl font-semibold">
        {t('admin.websiteTemplates.gallery.title')}
      </h1>

      <div className="flex min-w-0 items-center gap-3">
        {thumbnail === null ? (
          <span className="bg-admin-surface-muted text-muted-foreground flex size-10 items-center justify-center rounded-md">
            <LayoutTemplate aria-hidden="true" className="size-5" />
          </span>
        ) : (
          <img
            src={thumbnail}
            alt={t('admin.websiteTemplates.editor.templateThumbnailAlt', { name })}
            className="border-border h-10 w-14 rounded-md border object-cover"
          />
        )}
        <span className="text-foreground truncate text-sm font-medium" data-testid="template-name">
          {name}
        </span>
        <Badge
          variant={status === 'live' ? 'success' : 'neutral'}
          data-testid="template-status"
          data-status={status}
        >
          {status === 'live'
            ? t('admin.websiteTemplates.editor.status.live')
            : t('admin.websiteTemplates.editor.status.draft')}
        </Badge>
      </div>

      <div className="ms-auto flex min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-3">
        <Button type="button" variant="outline" onClick={onChangeTemplate}>
          {t('admin.websiteTemplates.editor.changeTemplate')}
        </Button>
        <EditModeSwitch enabled={editMode} onChange={onEditModeChange} />
        {onEditRegion ? (
          <EditableRegionsMenu
            key={editableRegionsKey}
            regions={editableRegions}
            disabled={!editMode || editingLocked}
            onEditRegion={onEditRegion}
          />
        ) : null}
        {/*
          PD12: publication state, save state, the action and "More options"
          stay together at the row end and wrap as one (fix round 2: the
          first edit of a live template keeps Publish changes on its row).
        */}
        <div
          className="ms-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-x-1.5 gap-y-2"
          data-testid="publish-group"
        >
          {hasUnpublishedChanges ? (
            <Badge variant="warning" data-testid="unpublished-changes">
              {t('admin.websiteTemplates.editor.status.unpublishedChanges')}
            </Badge>
          ) : null}
          <DraftSaveStatusChip
            snapshot={saveSnapshot}
            variant="toolbar"
            hidden={
              publishAction.label === 'allPublished' && draftSaveIndicator(saveSnapshot) === 'saved'
            }
            live={saveStatusLive}
            onRetry={onRetrySave}
            onReload={onReloadDraft}
            onGoToItem={onGoToItem}
          />
          <Button
            type="button"
            className="shrink-0 px-4"
            disabled={publishAction.disabled || isPublishing}
            onClick={onPublish}
            data-testid="publish-button"
            data-action={publishAction.label}
          >
            {isPublishing
              ? t('admin.websiteTemplates.editor.publishing')
              : t(PUBLISH_LABEL_KEYS[publishAction.label])}
          </Button>
          <OverflowMenu
            label={t('admin.websiteTemplates.editor.more')}
            items={[
              {
                key: 'language',
                label: t('admin.websiteTemplates.editor.storeLanguage'),
                onSelect: onOpenLanguage,
              },
              {
                key: 'live',
                label: t('admin.websiteTemplates.editor.openLive'),
                onSelect: onOpenLive,
                disabled: liveUrl === null,
                title:
                  liveUrl === null
                    ? t('admin.websiteTemplates.frame.openLiveUnavailable')
                    : undefined,
                // The reason is also visible under the item and its accessible description.
                description:
                  liveUrl === null
                    ? t('admin.websiteTemplates.frame.openLiveUnavailable')
                    : undefined,
              },
              ...(onUpdateTemplate
                ? [
                    {
                      key: 'update-template',
                      label: t('admin.websiteTemplates.editor.updateTemplate'),
                      onSelect: onUpdateTemplate,
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </div>
    </header>
  );
}
